import React, { useEffect, useState } from "react";
import axios from "axios";
import { makeStyles } from "@material-ui/core/styles";
import {
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  Paper,
  TextField,
  Button,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  IconButton,
  MenuItem,
} from "@material-ui/core";
import { ExitToApp as ExitToAppIcon } from "@material-ui/icons";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import EditIcon from "@material-ui/icons/Edit";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import GavelIcon from "@mui/icons-material/Gavel";
import Autocomplete from "@mui/material/Autocomplete";
import { useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { jwtDecode } from "jwt-decode";
import API_BASE_URL from "../../config/api";

const drawerWidth = 240;
const BASE_URL = API_BASE_URL;

const useStyles = makeStyles((theme) => ({
  root: { display: "flex", minHeight: "100vh" },
  drawer: { width: drawerWidth, flexShrink: 0 },
  drawerPaper: { width: drawerWidth },
  content: {
    flexGrow: 1,
    padding: theme.spacing(3),
    width: "100%",
    backgroundColor: "#fafafa",
  },
  paper: {
    padding: theme.spacing(3),
    width: "100%",
    maxWidth: "1200px",
    margin: "0 auto",
  },
  pageTitle: {
    textAlign: "center",
    marginBottom: theme.spacing(3),
    fontWeight: 700,
  },
  modeSelection: {
    display: "flex",
    justifyContent: "center",
    gap: theme.spacing(4),
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(4),
    flexWrap: "wrap",
  },
  modeCard: {
    width: 300,
    height: 210,
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    transition: "transform 0.2s, box-shadow 0.2s",
    "&:hover": {
      transform: "scale(1.03)",
      boxShadow: theme.shadows[8],
    },
  },
  modeTitle: {
    fontWeight: "bold",
    textAlign: "center",
  },
  modeIcon: {
    fontSize: 60,
    marginBottom: theme.spacing(2),
    color: theme.palette.primary.main,
  },
  backButton: {
    marginBottom: theme.spacing(2),
  },
  multilineField: {
    marginTop: theme.spacing(2),
  },
  hintBox: {
    padding: theme.spacing(2),
    backgroundColor: "#f5f5f5",
    borderRadius: 8,
    marginBottom: theme.spacing(2),
  },
}));

const initialFormData = {
  powodWpisania: "",
  dataSAD: "",
  mrn: "",
  nrAkt: "",
  nadawca: "",
  odbiorca: "",
  podpisany: "",
  rodzajSAD: "",
  procedura: "",
  kontrolujacy: "",
  kwalifikujeDoKorekty: "",
  dataUmieszczeniaWRejestrzeDs: "",
  dataZgloszeniaDoDias: "",
};

const getAuthHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("token")}`,
  "Content-Type": "application/json",
});

const getUserInfo = () => {
  try {
    const token = localStorage.getItem("token");

    if (!token) {
      return { username: "", role: "" };
    }

    const decoded = jwtDecode(token);

    return {
      username: decoded?.username || "",
      role: String(decoded?.role || "").toLowerCase(),
    };
  } catch (error) {
    return { username: "", role: "" };
  }
};

const KW_STATUS_OPTIONS = ["KW rozpoczęta", "KW zakończona"];

const isValidCreatedAtDateTime = (value) => {
  return /^(0[1-9]|[12][0-9]|3[01])\.(0[1-9]|1[0-2])\.\d{4} ([01][0-9]|2[0-3]):[0-5][0-9](:[0-5][0-9])?$/.test(
    String(value || "").trim(),
  );
};

const isValidDate = (value) => {
  return /^(0[1-9]|[12][0-9]|3[01])\.(0[1-9]|1[0-2])\.\d{4}$/.test(value);
};

const MIN_MRN_PREFIX_YEAR = 23;

const hasAllowedMrnPrefix = (normalizedValue) => {
  const match = String(normalizedValue || "").match(/^(\d{2})PL/);

  if (!match) {
    return false;
  }

  return Number(match[1]) >= MIN_MRN_PREFIX_YEAR;
};

const normalizeMrnValue = (value) =>
  value
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "")
    .slice(0, 20);

const isValidMrn = (value) => {
  const normalizedValue = normalizeMrnValue(value.trim());
  return hasAllowedMrnPrefix(normalizedValue) && /^[A-Z0-9]{18,20}$/.test(normalizedValue);
};

const isValidNrAkt = (value) => {
  const trimmedValue = String(value || "").trim();
  return trimmedValue === "" || /^[^/\s]+\/[^/\s]+$/.test(trimmedValue);
};

const normalizeListValue = (value) =>
  String(value || "")
    .trim()
    .toLowerCase();

const isValueOnList = (value, options = []) => {
  const normalizedValue = normalizeListValue(value);
  return options.some((item) => normalizeListValue(item) === normalizedValue);
};

const DodajDoKW = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const userInfo = getUserInfo();
  const isAdmin = userInfo.role === "admin";
  const [entryMode, setEntryMode] = useState(null);
  const [bulkValue, setBulkValue] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState(initialFormData);
  const [uzytkownicyList, setUzytkownicyList] = useState([]);
  const [powodyWpisaniaList, setPowodyWpisaniaList] = useState([]);
  const [rodzajeSADList, setRodzajeSADList] = useState([]);
  const [proceduryList, setProceduryList] = useState([]);
  const [nadawcyList, setNadawcyList] = useState([]);
  const [odbiorcyList, setOdbiorcyList] = useState([]);

  const hasInvalidDataSAD =
    formData.dataSAD.trim() !== "" && !isValidDate(formData.dataSAD.trim());
  const hasInvalidDataUmieszczeniaWRejestrzeDs =
    formData.dataUmieszczeniaWRejestrzeDs.trim() !== "" &&
    !isValidDate(formData.dataUmieszczeniaWRejestrzeDs.trim());
  const hasInvalidDataZgloszeniaDoDias =
    formData.dataZgloszeniaDoDias.trim() !== "" &&
    !isValidDate(formData.dataZgloszeniaDoDias.trim());
  const hasInvalidMRN =
    formData.mrn.trim() !== "" && !isValidMrn(formData.mrn.trim());
  const hasInvalidNrAkt =
    formData.nrAkt.trim() !== "" && !isValidNrAkt(formData.nrAkt.trim());

  useEffect(() => {
    const headers = getAuthHeaders();

    const fetchUsers = async () => {
      try {
        const response = await axios.get(`${BASE_URL}/list/uzytkownicy`, {
          headers,
        });

        const sortedUsers = Array.isArray(response.data)
          ? [...response.data].sort((a, b) =>
              (a?.name || "").localeCompare(b?.name || "", "pl", {
                sensitivity: "base",
              }),
            )
          : [];

        setUzytkownicyList(sortedUsers);
      } catch (error) {
        console.error("Błąd podczas pobierania listy użytkowników:", error);
        toast.error("Nie udało się pobrać listy użytkowników.");
      }
    };

    const mapApiDataToSortedNames = (data) =>
      Array.isArray(data)
        ? Array.from(
            new Set(data.map((item) => item?.name?.trim()).filter(Boolean)),
          ).sort((a, b) => a.localeCompare(b, "pl", { sensitivity: "base" }))
        : [];

    const fetchPowodyWpisania = async () => {
      try {
        const response = await axios.get(`${BASE_URL}/list/kwPowodyWpisania`, {
          headers,
        });
        setPowodyWpisaniaList(mapApiDataToSortedNames(response.data));
      } catch (error) {
        console.error("Błąd podczas pobierania listy powodów wpisania:", error);
      }
    };

    const fetchRodzajeSAD = async () => {
      try {
        const response = await axios.get(`${BASE_URL}/list/kwRodzajSad`, {
          headers,
        });
        setRodzajeSADList(mapApiDataToSortedNames(response.data));
      } catch (error) {
        console.error("Błąd podczas pobierania listy rodzajów SAD:", error);
      }
    };

    const fetchProcedury = async () => {
      try {
        const response = await axios.get(`${BASE_URL}/list/kwProcedura`, {
          headers,
        });
        setProceduryList(mapApiDataToSortedNames(response.data));
      } catch (error) {
        console.error("Błąd podczas pobierania listy procedur:", error);
      }
    };

    const fetchNadawcy = async () => {
      try {
        const response = await axios.get(`${BASE_URL}/list/eksportereksport`, {
          headers,
        });
        setNadawcyList(mapApiDataToSortedNames(response.data));
      } catch (error) {
        console.error("Błąd podczas pobierania listy nadawców:", error);
        toast.error("Nie udało się pobrać listy nadawców.");
      }
    };

    const fetchOdbiorcy = async () => {
      try {
        const response = await axios.get(`${BASE_URL}/list/importerimport`, {
          headers,
        });
        setOdbiorcyList(mapApiDataToSortedNames(response.data));
      } catch (error) {
        console.error("Błąd podczas pobierania listy odbiorców:", error);
        toast.error("Nie udało się pobrać listy odbiorców.");
      }
    };

    fetchUsers();
    fetchPowodyWpisania();
    fetchRodzajeSAD();
    fetchProcedury();
    fetchNadawcy();
    fetchOdbiorcy();
  }, []);

  const handleLogout = () => {
    localStorage.clear();
    toast.success("Wylogowano pomyślnie.", {
      autoClose: 500,
      onClose: () => navigate("/login"),
    });
  };

  const handleBackToTableClick = () => {
    navigate("/kontrolaWewnetrzna");
  };

  const handleBackToSelection = () => {
    setEntryMode(null);
    setBulkValue("");
    setFormData(initialFormData);
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    const normalizedValue = name === "mrn" ? normalizeMrnValue(value) : value;
    setFormData((prev) => ({ ...prev, [name]: normalizedValue }));
  };

  const handleKeyDown = (event) => {
    if (event.key === "Tab") {
      event.preventDefault();
      const { selectionStart, selectionEnd, value } = event.target;
      const updatedValue =
        value.substring(0, selectionStart) +
        "\t" +
        value.substring(selectionEnd);

      setBulkValue(updatedValue);

      requestAnimationFrame(() => {
        event.target.selectionStart = selectionStart + 1;
        event.target.selectionEnd = selectionStart + 1;
      });
    }
  };

  const buildEntry = (data) => ({
    powodWpisania: data.powodWpisania?.trim() || "",
    dataSAD: data.dataSAD?.trim() || "",
    mrn: normalizeMrnValue(data.mrn?.trim() || ""),
    nrAkt: data.nrAkt?.trim() || "",
    nadawca: data.nadawca?.trim() || "",
    odbiorca: data.odbiorca?.trim() || "",
    podpisany: data.podpisany?.trim() || "",
    rodzajSAD: data.rodzajSAD?.trim() || "",
    procedura: data.procedura?.trim() || "",
    kontrolujacy: data.kontrolujacy?.trim() || "",
    kwalifikujeDoKorekty: data.kwalifikujeDoKorekty?.trim() || "",
    dataUmieszczeniaWRejestrzeDs:
      data.dataUmieszczeniaWRejestrzeDs?.trim() || "",
    dataZgloszeniaDoDias: data.dataZgloszeniaDoDias?.trim() || "",
  });

  const handleManualSubmit = async (event) => {
    event.preventDefault();

    const requiredValues = [
      formData.powodWpisania,
      formData.dataSAD,
      formData.mrn,
      formData.nadawca,
      formData.odbiorca,
      formData.podpisany,
      formData.rodzajSAD,
      formData.procedura,
      formData.kontrolujacy,
    ];

    if (requiredValues.some((value) => !String(value).trim())) {
      toast.warning("Uzupełnij wszystkie wymagane pola formularza.");
      return;
    }

    if (!isValidDate(formData.dataSAD)) {
      toast.warning("Data SAD musi być w formacie dd.mm.yyyy.");
      return;
    }

    if (!isValidMrn(formData.mrn)) {
      toast.warning(
        `MRN musi zaczynać się od XXPL, gdzie XX >= ${MIN_MRN_PREFIX_YEAR} (np. 23PL, 24PL) i mieć 18-20 znaków (tylko litery i cyfry).`,
      );
      return;
    }

    if (!isValidNrAkt(formData.nrAkt)) {
      toast.warning("Jeśli podasz Nr akt, musi mieć format dane1/dane2.");
      return;
    }

    if (isAdmin && hasInvalidDataUmieszczeniaWRejestrzeDs) {
      toast.warning(
        "Data umieszczenia w rejestrze DS musi mieć format dd.mm.yyyy.",
      );
      return;
    }

    if (isAdmin && hasInvalidDataZgloszeniaDoDias) {
      toast.warning("Data zgłoszenia do DIAS musi mieć format dd.mm.yyyy.");
      return;
    }

    setIsSubmitting(true);

    try {
      const newEntry = buildEntry(formData);
      const response = await axios.post(`${BASE_URL}/kw-dane`, newEntry, {
        headers: getAuthHeaders(),
      });

      if (response.data?.isDuplicate) {
        toast.warning(
          response.data?.message ||
            "Wpis zapisano, ale MRN już istnieje i rekord oznaczono jako duplikat.",
        );
      } else {
        toast.success(response.data?.message || "Wpis do KW został zapisany.");
      }

      setFormData(initialFormData);
      navigate("/kontrolaWewnetrzna");
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Nie udało się zapisać wpisu do KW.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBulkSubmit = async (event) => {
    event.preventDefault();

    if (!bulkValue.trim()) {
      toast.warning("Wklej dane do pola wielu pozycji.");
      return;
    }

    setIsSubmitting(true);

    try {
      const isAdminBulkMode = entryMode === "bulkAdmin";
      const lines = bulkValue
        .split("\n")
        .map((line) => line.trim())
        .filter(Boolean);

      const userNames = uzytkownicyList
        .map((item) => item?.name)
        .filter(Boolean);

      if (
        !powodyWpisaniaList.length ||
        !rodzajeSADList.length ||
        !proceduryList.length ||
        !userNames.length
      ) {
        throw new Error(
          "Nie udało się załadować list potrzebnych do walidacji danych z Excela.",
        );
      }

      const parsedEntries = lines.map((line, index) => {
        const values = line.split("\t");

        const expectedColumns = isAdminBulkMode ? 14 : 10;

        if (values.length !== expectedColumns) {
          throw new Error(
            `Wiersz ${index + 1} ma nieprawidłową liczbę kolumn. Oczekiwano dokładnie ${expectedColumns} wartości.`,
          );
        }

        const mapped = {
          powodWpisania: values[0],
          dataSAD: values[1],
          mrn: values[2],
          nrAkt: values[3],
          nadawca: values[4],
          odbiorca: values[5],
          podpisany: values[6] || "",
          rodzajSAD: values[7],
          procedura: values[8],
          kontrolujacy: values[9],
          kwalifikujeDoKorekty: "",
          createdAt: isAdminBulkMode ? values[10] : "",
          status: isAdminBulkMode ? values[11] : "",
          dataUmieszczeniaWRejestrzeDs: isAdminBulkMode ? values[12] : "",
          dataZgloszeniaDoDias: isAdminBulkMode ? values[13] : "",
        };

        const requiredValues = [
          mapped.powodWpisania,
          mapped.dataSAD,
          mapped.mrn,
          mapped.nadawca,
          mapped.odbiorca,
          mapped.podpisany,
          mapped.rodzajSAD,
          mapped.procedura,
          mapped.kontrolujacy,
        ];

        if (requiredValues.some((value) => !String(value).trim())) {
          throw new Error(`Wiersz ${index + 1} zawiera puste wymagane pola.`);
        }

        if (!isValidDate(mapped.dataSAD)) {
          throw new Error(`Wiersz ${index + 1} ma niepoprawną datę SAD.`);
        }

        if (!isValidMrn(mapped.mrn)) {
          throw new Error(
            `Wiersz ${index + 1} ma niepoprawny MRN. Musi zaczynać się od XXPL, gdzie XX >= ${MIN_MRN_PREFIX_YEAR} (np. 23PL, 24PL) i mieć 18-20 znaków alfanumerycznych.`,
          );
        }

        if (!isValidNrAkt(mapped.nrAkt)) {
          throw new Error(
            `Wiersz ${index + 1} ma niepoprawne pole Nr akt. Użyj formatu dane1/dane2.`,
          );
        }

        if (isAdminBulkMode && !isValidCreatedAtDateTime(mapped.createdAt)) {
          throw new Error(
            `Wiersz ${index + 1} ma niepoprawną datę utworzenia. Użyj formatu dd.mm.yyyy hh:mm lub dd.mm.yyyy hh:mm:ss.`,
          );
        }

        if (
          isAdminBulkMode &&
          !KW_STATUS_OPTIONS.includes(String(mapped.status || "").trim())
        ) {
          throw new Error(
            `Wiersz ${index + 1} ma niepoprawny status. Dozwolone wartości: ${KW_STATUS_OPTIONS.join(", ")}.`,
          );
        }

        if (
          isAdminBulkMode &&
          String(mapped.dataUmieszczeniaWRejestrzeDs || "").trim() &&
          !isValidDate(mapped.dataUmieszczeniaWRejestrzeDs.trim())
        ) {
          throw new Error(
            `Wiersz ${index + 1} ma niepoprawny format daty umieszczenia w rejestrze DS. Użyj formatu dd.mm.yyyy lub zostaw puste.`,
          );
        }

        if (
          isAdminBulkMode &&
          String(mapped.dataZgloszeniaDoDias || "").trim() &&
          !isValidDate(mapped.dataZgloszeniaDoDias.trim())
        ) {
          throw new Error(
            `Wiersz ${index + 1} ma niepoprawny format daty zgłoszenia do DIAS. Użyj formatu dd.mm.yyyy lub zostaw puste.`,
          );
        }

        const fieldChecks = [
          {
            label: "Powód wpisania",
            value: mapped.powodWpisania,
            options: powodyWpisaniaList,
          },
          {
            label: "Podpisany",
            value: mapped.podpisany,
            options: userNames,
          },
          {
            label: "Rodzaj SAD",
            value: mapped.rodzajSAD,
            options: rodzajeSADList,
          },
          {
            label: "Procedura",
            value: mapped.procedura,
            options: proceduryList,
          },
          {
            label: "Kontrolujący",
            value: mapped.kontrolujacy,
            options: userNames,
          },
        ];

        const invalidField = fieldChecks.find(
          ({ value, options }) => !isValueOnList(value, options),
        );

        if (invalidField) {
          throw new Error(
            `Wiersz ${index + 1}: wartość "${invalidField.value}" w polu "${invalidField.label}" nie istnieje na liście dozwolonych wartości.`,
          );
        }

        const entry = buildEntry(mapped);

        if (isAdminBulkMode) {
          entry.createdAt = mapped.createdAt.trim();
          entry.status = mapped.status.trim();
          entry.dataUmieszczeniaWRejestrzeDs = String(
            mapped.dataUmieszczeniaWRejestrzeDs || "",
          ).trim();
          entry.dataZgloszeniaDoDias = String(
            mapped.dataZgloszeniaDoDias || "",
          ).trim();
        }

        return entry;
      });

      const response = await axios.post(
        `${BASE_URL}/kw-dane/bulk`,
        { entries: parsedEntries },
        {
          headers: getAuthHeaders(),
        },
      );

      if ((response.data?.duplicateCount || 0) > 0) {
        toast.warning(
          response.data?.message ||
            `Dodano ${parsedEntries.length} wpisów do KW. Część oznaczono jako duplikat.`,
        );
      } else {
        toast.success(
          response.data?.message ||
            `Dodano ${parsedEntries.length} wpisów do KW.`,
        );
      }

      setBulkValue("");
      navigate("/kontrolaWewnetrzna");
    } catch (error) {
      toast.error(
        error.response?.data?.message ||
          error.message ||
          "Nie udało się przetworzyć danych zbiorczych.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={classes.root}>
      <Drawer
        className={classes.drawer}
        variant="permanent"
        classes={{ paper: classes.drawerPaper }}
      >
        <Toolbar style={{ minHeight: 3 }} />
        <List>
          <ListItem button onClick={handleLogout}>
            <ListItemIcon>
              <ExitToAppIcon />
            </ListItemIcon>
            <ListItemText primary="Wyloguj" />
          </ListItem>
          <ListItem button onClick={handleBackToTableClick}>
            <ListItemIcon>
              <ArrowBackIcon />
            </ListItemIcon>
            <ListItemText primary="Powrót do KW" />
          </ListItem>
        </List>
      </Drawer>

      <main className={classes.content}>
        <Toolbar />
        <Typography variant="h4" className={classes.pageTitle}>
          <GavelIcon style={{ verticalAlign: "middle", marginRight: 8 }} />
          Dodaj do Kontroli wewnętrznej
        </Typography>

        <Paper className={classes.paper}>
          {entryMode && (
            <IconButton
              className={classes.backButton}
              onClick={handleBackToSelection}
            >
              <ArrowBackIcon />
            </IconButton>
          )}

          {!entryMode && (
            <>
              <Typography variant="h6" align="center">
                Wybierz sposób dodawania wpisów
              </Typography>
              <div className={classes.modeSelection}>
                <Card className={classes.modeCard}>
                  <CardActionArea onClick={() => setEntryMode("manual")}>
                    <CardContent
                      style={{ textAlign: "center", paddingTop: 40 }}
                    >
                      <EditIcon className={classes.modeIcon} />
                      <Typography variant="h6" className={classes.modeTitle}>
                        Dodaj jedną pozycję
                      </Typography>
                    </CardContent>
                  </CardActionArea>
                </Card>

                <Card className={classes.modeCard}>
                  <CardActionArea onClick={() => setEntryMode("bulk")}>
                    <CardContent
                      style={{ textAlign: "center", paddingTop: 40 }}
                    >
                      <FileCopyIcon className={classes.modeIcon} />
                      <Typography variant="h6" className={classes.modeTitle}>
                        Dodaj wiele pozycji
                      </Typography>
                    </CardContent>
                  </CardActionArea>
                </Card>

                {isAdmin && (
                  <Card className={classes.modeCard}>
                    <CardActionArea onClick={() => setEntryMode("bulkAdmin")}>
                      <CardContent
                        style={{ textAlign: "center", paddingTop: 40 }}
                      >
                        <FileCopyIcon className={classes.modeIcon} />
                        <Typography variant="h6" className={classes.modeTitle}>
                          Dodaj wiele pozycji admin
                        </Typography>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                )}
              </div>
            </>
          )}

          {entryMode === "manual" && (
            <form onSubmit={handleManualSubmit}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    select
                    fullWidth
                    required
                    label="Powód wpisania"
                    name="powodWpisania"
                    value={formData.powodWpisania}
                    onChange={handleInputChange}
                  >
                    <MenuItem value="" style={{ fontWeight: "bold" }}>
                      Wybierz powód wpisania
                    </MenuItem>
                    {powodyWpisaniaList.map((item) => (
                      <MenuItem key={item} value={item}>
                        {item}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    required
                    label="Data SAD"
                    name="dataSAD"
                    placeholder="dd.mm.yyyy"
                    value={formData.dataSAD}
                    onChange={handleInputChange}
                    error={hasInvalidDataSAD}
                    helperText={
                      hasInvalidDataSAD
                        ? "Niepoprawny format daty. Użyj dd.mm.yyyy"
                        : "Wymagany format: dd.mm.yyyy"
                    }
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    required
                    label="MRN"
                    name="mrn"
                    value={formData.mrn}
                    onChange={handleInputChange}
                    error={hasInvalidMRN}
                    helperText={
                      hasInvalidMRN
                        ? `MRN musi zaczynać się od XXPL, gdzie XX >= ${MIN_MRN_PREFIX_YEAR} (np. 23PL, 24PL) i mieć 18-20 znaków bez spacji i znaków specjalnych.`
                        : `Wymagany format: XXPL... (XX >= ${MIN_MRN_PREFIX_YEAR}, 18-20 znaków)`
                    }
                    inputProps={{ maxLength: 20 }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Dane z pola 7 SAD"
                    name="nrAkt"
                    value={formData.nrAkt}
                    onChange={handleInputChange}
                    error={hasInvalidNrAkt}
                    helperText={
                      hasInvalidNrAkt
                        ? "Jeśli wpisujesz Nr akt, użyj formatu nr własny/nr ref"
                        : "Pole opcjonalne. Format: nr własny/nr ref"
                    }
                    placeholder="np. ABC/12345"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Autocomplete
                    fullWidth
                    options={nadawcyList}
                    value={formData.nadawca || null}
                    onChange={(_, newValue) =>
                      setFormData((prev) => ({
                        ...prev,
                        nadawca: newValue || "",
                      }))
                    }
                    openOnFocus
                    autoHighlight
                    noOptionsText="Brak wyników"
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        fullWidth
                        required
                        label="Nadawca"
                        helperText="Kliknij i wpisz, aby wyszukać nadawcę"
                        inputProps={{
                          ...params.inputProps,
                          autoComplete: "off",
                        }}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Autocomplete
                    fullWidth
                    options={odbiorcyList}
                    value={formData.odbiorca || null}
                    onChange={(_, newValue) =>
                      setFormData((prev) => ({
                        ...prev,
                        odbiorca: newValue || "",
                      }))
                    }
                    openOnFocus
                    autoHighlight
                    noOptionsText="Brak wyników"
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        fullWidth
                        required
                        label="Odbiorca"
                        helperText="Kliknij i wpisz, aby wyszukać odbiorcę"
                        inputProps={{
                          ...params.inputProps,
                          autoComplete: "off",
                        }}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    select
                    fullWidth
                    required
                    label="Podpisany"
                    name="podpisany"
                    value={formData.podpisany}
                    onChange={handleInputChange}
                  >
                    <MenuItem value="" style={{ fontWeight: "bold" }}>
                      Wybierz użytkownika
                    </MenuItem>
                    {uzytkownicyList.map((item) => (
                      <MenuItem key={item.id} value={item.name}>
                        {item.name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    select
                    fullWidth
                    required
                    label="Rodzaj SAD"
                    name="rodzajSAD"
                    value={formData.rodzajSAD}
                    onChange={handleInputChange}
                  >
                    <MenuItem value="" style={{ fontWeight: "bold" }}>
                      Wybierz rodzaj SAD
                    </MenuItem>
                    {rodzajeSADList.map((item) => (
                      <MenuItem key={item} value={item}>
                        {item}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    select
                    fullWidth
                    required
                    label="Procedura"
                    name="procedura"
                    value={formData.procedura}
                    onChange={handleInputChange}
                  >
                    <MenuItem value="" style={{ fontWeight: "bold" }}>
                      Wybierz procedurę
                    </MenuItem>
                    {proceduryList.map((item) => (
                      <MenuItem key={item} value={item}>
                        {item}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    select
                    fullWidth
                    required
                    label="Kontrolujący"
                    name="kontrolujacy"
                    value={formData.kontrolujacy}
                    onChange={handleInputChange}
                  >
                    <MenuItem value="" style={{ fontWeight: "bold" }}>
                      Wybierz użytkownika
                    </MenuItem>
                    {uzytkownicyList.map((item) => (
                      <MenuItem key={item.id} value={item.name}>
                        {item.name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                {isAdmin && (
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Data umieszczenia w rejestrze DS"
                      name="dataUmieszczeniaWRejestrzeDs"
                      placeholder="dd.mm.yyyy"
                      value={formData.dataUmieszczeniaWRejestrzeDs}
                      onChange={handleInputChange}
                      error={hasInvalidDataUmieszczeniaWRejestrzeDs}
                      helperText={
                        hasInvalidDataUmieszczeniaWRejestrzeDs
                          ? "Niepoprawny format daty. Użyj dd.mm.yyyy"
                          : "Pole opcjonalne. Format: dd.mm.yyyy"
                      }
                    />
                  </Grid>
                )}
                {isAdmin && (
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Data zgłoszenia do DIAS"
                      name="dataZgloszeniaDoDias"
                      placeholder="dd.mm.yyyy"
                      value={formData.dataZgloszeniaDoDias}
                      onChange={handleInputChange}
                      error={hasInvalidDataZgloszeniaDoDias}
                      helperText={
                        hasInvalidDataZgloszeniaDoDias
                          ? "Niepoprawny format daty. Użyj dd.mm.yyyy"
                          : "Pole opcjonalne. Format: dd.mm.yyyy"
                      }
                    />
                  </Grid>
                )}
              </Grid>

              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={
                  isSubmitting ||
                  hasInvalidDataSAD ||
                  hasInvalidDataUmieszczeniaWRejestrzeDs ||
                  hasInvalidDataZgloszeniaDoDias ||
                  hasInvalidMRN ||
                  hasInvalidNrAkt
                }
                style={{ marginTop: 24 }}
              >
                Zapisz wpis
              </Button>
            </form>
          )}

          {(entryMode === "bulk" || entryMode === "bulkAdmin") && (
            <form onSubmit={handleBulkSubmit}>
              <div className={classes.hintBox}>
                <Typography variant="body1">
                  Każdy rekord wpisz w osobnym wierszu, a pola oddziel
                  tabulatorem.
                </Typography>
                <Typography variant="body2" style={{ marginTop: 8 }}>
                  {entryMode === "bulkAdmin"
                    ? "Kolejność pól: Powód wpisania, Data SAD, MRN, Nr akt, Nadawca, Odbiorca, Podpisany, rodzaj SAD, procedura, kontrolujący, Data utworzenia, Status, Data umieszczenia w rejestrze DS, Data zgłoszenia do DIAS. W trybie zbiorczym pola Nadawca i Odbiorca nie są sprawdzane względem listy."
                    : "Kolejność pól: Powód wpisania, Data SAD, MRN, Nr akt, Nadawca, Odbiorca, Podpisany, rodzaj SAD, procedura, kontrolujący. W trybie zbiorczym pola Nadawca i Odbiorca nie są sprawdzane względem listy."}
                </Typography>
                <Typography variant="body2" style={{ marginTop: 8 }}>
                  Pole Podpisany i kontrolujący muszą być uzupełnione zgodnie z
                  listą użytkowników.
                </Typography>
                {entryMode === "bulkAdmin" && (
                  <Typography variant="body2" style={{ marginTop: 8 }}>
                    Data utworzenia musi mieć format dd.mm.yyyy hh:mm lub
                    dd.mm.yyyy hh:mm:ss, a status musi być równy: KW rozpoczęta
                    albo KW zakończona. Kolumny 13 i 14 (daty DS i DIAS) są
                    opcjonalne — możesz je pominąć wpisując pustą wartość.
                  </Typography>
                )}
              </div>

              <TextField
                fullWidth
                multiline
                rows={12}
                variant="outlined"
                value={bulkValue}
                onChange={(event) => setBulkValue(event.target.value)}
                onKeyDown={handleKeyDown}
                className={classes.multilineField}
                placeholder={
                  entryMode === "bulkAdmin"
                    ? "Powód wpisania\t15.04.2026\t23PL1234567890ABCD\tAKT/12345\tNadawca A\tOdbiorca A\tJan Kowalski\tImport\t4200\tAnna Nowak\t27.04.2026 08:30\tKW rozpoczęta\t28.04.2026\t29.04.2026"
                    : "Powód wpisania\t15.04.2026\t23PL1234567890ABCD\tAKT/12345\tNadawca A\tOdbiorca A\tJan Kowalski\tImport\t4200\tAnna Nowak"
                }
              />

              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={isSubmitting}
                style={{ marginTop: 24 }}
              >
                Dodaj rekordy
              </Button>
            </form>
          )}
        </Paper>
        <ToastContainer position="top-right" />
      </main>
    </div>
  );
};

export default DodajDoKW;
